import './assets/base.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createHead } from '@unhead/vue/client'
import { Frontegg } from '@frontegg/vue'
import { CONFIG } from '@/config/index'
import PrimeVue from 'primevue/config'
import 'primeicons/primeicons.css'
import ToastService from 'primevue/toastservice'
import Toast from 'primevue/toast'
import ConfirmationService from 'primevue/confirmationservice'
import ConfirmDialog from 'primevue/confirmdialog'
import App from './App.vue'
import router from '@/router/index'
import Tooltip from 'primevue/tooltip'
import { useThemeStore } from '@/stores/themeStore'
import { initDatadogRum, setupAxiosTracing } from '@/util/datadogRum'

export const hasPerms = function (perms) {
  try {
    const mapped = app.mapAuthState ? app.mapAuthState() : null
    const authState = mapped ? mapped.authState : null
    const permissions = authState?.user?.permissions
    if (!Array.isArray(permissions)) return false
    return permissions.some(e => e?.key === perms)
  } catch (error) {
    console.error('Error getting permissions:', error)
    return false
  }
}

const app = createApp(App)
const head = createHead()
const pinia = createPinia()

useThemeStore(pinia).init()

app
  .use(head)
  .use(pinia)
  .use(router)
  .use(Frontegg, {
    contextOptions: {
      baseUrl: CONFIG.FRONTEGG.baseUrl,
      clientId: CONFIG.FRONTEGG.clientId,
    },
    authOptions: {
      keepSessionAlive: true,
    },
    hostedLoginBox: true,
    router,
  })
  .use(PrimeVue, {
    theme: 'none',
  })
  .use(ToastService)
  .component('Toast', Toast)
  .use(ConfirmationService)
  .component('ConfirmDialog', ConfirmDialog)

app.directive('tooltip', Tooltip)

// Global auth guard: wait for access token on routes requiring permissions
router.beforeEach(async (to, from, next) => {
  const needsAuth = Boolean(to.meta && to.meta.perm)
  if (!needsAuth) return next()

  const waitForToken = async (timeoutMs = 7000) => {
    const start = Date.now()
    while (true) {
      try {
        const mapped = app.mapAuthState ? app.mapAuthState() : null
        const token = mapped?.authState?.user?.accessToken
        if (token) return true
        if (Date.now() - start > timeoutMs) return false
        // sleep 100ms

        await new Promise(resolve => setTimeout(resolve, 100))
      } catch (e) {
        console.error('Error getting permissions:', e)
        if (Date.now() - start > timeoutMs) return false

        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }
  }

  const ready = await waitForToken()
  if (!ready) {
    return next({ name: 'home' })
  }

  // Check if user has required permission
  if (to.meta.perm && !hasPerms(to.meta.perm)) {
    const toast = app.config.globalProperties.$toast
    if (toast) {
      toast.add({
        severity: 'warn',
        summary: 'Access Denied',
        detail: 'You do not have permission to access this page',
        life: 5000,
      })
    }
    return next({ name: 'home' })
  }

  return next()
})

app.provide('$toast', app.config.globalProperties.$toast)
app.provide('$confirm', app.config.globalProperties.$confirm)

// Non-prod only: exposes the live router tree so a Playwright spec
// (route-manifest-drift.spec.ts) can compare it against the checked-in
// `playwright/permissions/route-manifest.ts`. A new/renamed/re-permissioned
// route that isn't reflected in the checked-in manifest fails that spec.
// Vite dead-code-eliminates this block from a `prod` build (import.meta.env.MODE
// is statically known at build time).
if (import.meta.env.MODE !== 'prod') {
  const flattenRoutes = routes =>
    routes.flatMap(route => {
      const entry = route.name
        ? [
            {
              name: route.name,
              perm: route.meta?.perm ?? null,
              showInSidebar: Boolean(route.meta?.showInSidebar),
            },
          ]
        : []
      return entry.concat(route.children ? flattenRoutes(route.children) : [])
    })

  window.__APP_ROUTES__ = flattenRoutes(router.options.routes)
}

app.mount('#app')

// Initialize Datadog RUM in production only (avoids dev/sandbox traffic in prod project)
if (
  import.meta.env.MODE === 'prod' &&
  CONFIG.DATADOG.applicationId &&
  CONFIG.DATADOG.clientToken
) {
  initDatadogRum({
    applicationId: CONFIG.DATADOG.applicationId,
    clientToken: CONFIG.DATADOG.clientToken,
    site: CONFIG.DATADOG.site,
    service: CONFIG.DATADOG.service,
    env: CONFIG.DATADOG.env,
    sessionSampleRate: CONFIG.DATADOG.sessionSampleRate,
    sessionReplaySampleRate: CONFIG.DATADOG.sessionReplaySampleRate,
    // Note: We manually inject trace headers via createTracedHeaders() in service layer
    // to have access to trace IDs for error display. Don't use allowedTracingUrls here
    // as it would cause duplicate headers.
  })
}

// Install global Axios interceptor to inject DataDog trace headers on all requests.
// This ensures every Axios-based service automatically gets trace ID correlation
// without needing per-service changes. The trace ID is extracted from error.config.headers
// during error normalization.
setupAxiosTracing()
